export const calculateLumpSumSettlement = (payload, taxInformationData) => {
  const nextPayload = { ...payload };

  const totalSettlement = parseFloat(nextPayload["Gross Monthly Wages"] || 0);
  const legalFees = parseFloat(nextPayload["Legal Fees Incurred"] || 0);
  const medicalExpenses = parseFloat(nextPayload["Medical Expenses Paid for Distress"] || 0);
  const settlementBasis = nextPayload["Settlement Basis"];
  const paymentType = nextPayload["Payment Type"];

  // 1. Duration Calculation
  const startDate = nextPayload["Date Income Starts"] ? new Date(nextPayload["Date Income Starts"]) : null;
  const endDate = nextPayload["Date Income Ends"] ? new Date(nextPayload["Date Income Ends"]) : null;
  
  let durationYears = 1;
  if (startDate && endDate && endDate > startDate) {
    durationYears = (endDate - startDate) / (1000 * 60 * 60 * 24 * 365.25);
  }

  // Total Settlement Liability is the total gross settlement amount
  nextPayload["Total Settlement Liability"] = totalSettlement;

  // 2. Taxability Logic based on Settlement Basis
  let amountSubjectToTax = 0;
  let federalTaxType = "Ordinary Income";
  let stateTaxType = "Ordinary Income";
  let ficaTax = 0;

  const basis = settlementBasis || "";

  if (basis.includes("Personal Physical Injury")) {
    // Selection 1: Tax-Exempt. Amount Subject to Tax = 0.
    amountSubjectToTax = 0;
    federalTaxType = "Tax-Exempt";
    stateTaxType = "Tax Free";
  } else if (basis.includes("Emotional Distress")) {
    // Selection 2: Ordinary Income. Taxable = Total - Medical Expenses.
    amountSubjectToTax = Math.max(0, totalSettlement - medicalExpenses);
    federalTaxType = "Ordinary Income";
    stateTaxType = "Ordinary Income";
  } else if (basis.includes("Punitive Damages")) {
    // Selection 3: Ordinary Income. Taxable = Total Settlement.
    amountSubjectToTax = totalSettlement;
    federalTaxType = "Ordinary Income";
    stateTaxType = "Ordinary Income";
  } else if (basis.includes("Back Pay") || basis.includes("Severance")) {
    // Selection 4: W-2 Wages. Taxable = Total - Legal Fees.
    amountSubjectToTax = Math.max(0, totalSettlement - legalFees);
    federalTaxType = "W-2 Wages (Fully Taxable)";
    stateTaxType = "Ordinary Income";

    // "The FICA Trap": 7.65% must be calculated on the Total Settlement Amount (Gross)
    ficaTax = parseFloat((totalSettlement * 0.0765).toFixed(2));
  } else if (basis.includes("Civil Rights") || basis.includes("Whistleblower")) {
    // Selection 5: Ordinary Income. Above-the-Line Deduction for Legal Fees.
    amountSubjectToTax = Math.max(0, totalSettlement - legalFees);
    federalTaxType = "Ordinary Income";
    stateTaxType = "Ordinary Income";
  } else {
    amountSubjectToTax = totalSettlement;
  }

  nextPayload["Amount Subject to Federal Taxation"] = parseFloat(amountSubjectToTax.toFixed(2));
  nextPayload["Federal Taxation Type"] = federalTaxType;
  nextPayload["State Taxation Type"] = stateTaxType;

  // 3. Tax Estimation
  let fedTax = 0;
  let stateTax = 0;

  if (amountSubjectToTax > 0 && taxInformationData) {
    const fedRate = parseFloat(taxInformationData["Federal Tax Rate"] || 0) / 100;
    const stateRate = parseFloat(taxInformationData["State Tax Effective Rate"] || 0) / 100;
    
    fedTax = amountSubjectToTax * fedRate;
    stateTax = amountSubjectToTax * stateRate;
  }

  // 4. Net Amount Calculation
  // Net = Total Settlement - Legal Fees - (Federal Tax + State Tax + FICA)
  const netAmount = totalSettlement - legalFees - fedTax - stateTax - ficaTax;
  
  if (paymentType && paymentType.includes("Structured Settlement (Monthly)")) {
    nextPayload["Frequency of Pay Periods"] = "Monthly";
    const totalMonths = durationYears * 12 > 0 ? durationYears * 12 : 1;
    
    const monthlyNet = netAmount / totalMonths;
    const monthlyGross = totalSettlement / totalMonths;

    nextPayload["Monthly Portion of Net Amount"] = parseFloat(monthlyNet.toFixed(2));
    nextPayload["Monthly Portion of Gross"] = parseFloat(monthlyGross.toFixed(2));
    nextPayload["Net Amount"] = parseFloat(netAmount.toFixed(2));
    
    // Fallback for dashboard bar chart - many systems look for "Monthly Income"
    nextPayload["Monthly Income"] = parseFloat(monthlyNet.toFixed(2));
  } else {
    nextPayload["Frequency of Pay Periods"] = "One-Time";
    nextPayload["Monthly Portion of Net Amount"] = parseFloat(netAmount.toFixed(2));
    nextPayload["Monthly Portion of Gross"] = parseFloat(totalSettlement.toFixed(2));
    nextPayload["Net Amount"] = parseFloat(netAmount.toFixed(2));
    nextPayload["Monthly Income"] = 0;
  }

  return nextPayload;
};

export const getLumpSumSettlementConfig = (name) => {
  const config = {
    "Gross Monthly Wages": { label: "Total Settlement Amount" },
    "Payment Type": { label: "Payment Type" },
    "Settlement Basis": { label: "Settlement Basis" },
    "Legal Fees Incurred": { label: "Legal Fees Incurred" },
    "Medical Expenses Paid for Distress": { label: "Medical Expenses Paid for Distress" },
    "Amount Subject to Federal Taxation": { readOnly: true },
    "Net Amount": { label: "Net Amount (After Tax)", readOnly: true },
    "Monthly Portion of Net Amount": { label: "Monthly Portion of the Net Amount", readOnly: true },
    "Total Settlement Liability": { readOnly: true },
    
    // Disable/Remove requested fields
    "Passive or Active": { delete: true },
    "Amount Subject to Federal Taxation": { readOnly: true },
  };
  return config[name] || {};
};

export const getLumpSumSettlementGroupPrefix = (groupName) => {
  if (groupName === "INCOME DETAILS") return "Part A: ";
  if (groupName === "TAXATION") return "Part B: ";
  return "";
};
